import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

function isNumeric(val) {
  return typeof val === 'number' || (typeof val === 'string' && !isNaN(val) && val.trim() !== '');
}

function detectBarChartColumns(columns, rows) {
  if (!columns || columns.length !== 2 || !rows || rows.length === 0) return null;
  const numericCounts = columns.map(col => rows.filter(r => isNumeric(r[col])).length);
  const labelCol = numericCounts[0] < numericCounts[1] ? columns[0] : columns[1];
  const valueCol = labelCol === columns[0] ? columns[1] : columns[0];
  if (numericCounts[columns.indexOf(valueCol)] < rows.length * 0.8) return null;
  return { labelCol, valueCol };
}

export default function DataVisualization({ data }) {
  if (!data || !data.columns || !data.rows || data.rows.length === 0) return null;

  const barCols = detectBarChartColumns(data.columns, data.rows);

  if (barCols) {
    const maxValue = Math.max(...data.rows.map(r => Number(r[barCols.valueCol]) || 0)) || 1;
    return (
      <DataCard>
        <BarChart>
          {data.rows.map((row, i) => (
            <BarRow key={i}>
              <BarLabel>{row[barCols.labelCol] || 'Unknown'}</BarLabel>
              <BarContainer>
                <Bar style={{ width: `${((Number(row[barCols.valueCol]) || 0) / maxValue) * 100}%` }} />
                <BarValue>{row[barCols.valueCol]}</BarValue>
              </BarContainer>
            </BarRow>
          ))}
        </BarChart>
      </DataCard>
    );
  }

  return (
    <DataCard>
      <DataTable>
        <thead>
          <tr>
            {data.columns.map(col => (
              <th key={col}>{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row, i) => (
            <tr key={i}>
              {data.columns.map(col => (
                <td key={col}>{row[col]}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </DataTable>
    </DataCard>
  );
}

DataVisualization.propTypes = {
  data: PropTypes.shape({
    columns: PropTypes.arrayOf(PropTypes.string).isRequired,
    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  }),
};

const DataCard = styled.div`
  background: ${p => p.theme.colors.white};
  border: 1px solid ${p => p.theme.colors.gray300};
  border-radius: 8px;
  overflow: hidden;
  width: 100%;
`;

const DataTable = styled.table`
  border-collapse: collapse;
  font-size: 13px;
  width: 100%;

  th {
    background: ${p => p.theme.colors.gray50};
    border-bottom: 1px solid ${p => p.theme.colors.gray300};
    color: ${p => p.theme.colors.gray600};
    font-weight: ${p => p.theme.fontWeight.medium};
    padding: 8px 12px;
    text-align: left;
  }

  td {
    border-bottom: 1px solid ${p => p.theme.colors.gray200};
    color: ${p => p.theme.colors.gray800};
    padding: 8px 12px;
  }

  tr:last-child td {
    border-bottom: none;
  }

  tr:hover td {
    background: ${p => p.theme.colors.gray50};
  }
`;

const BarChart = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
`;

const BarRow = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
`;

const BarLabel = styled.div`
  color: ${p => p.theme.colors.gray700};
  flex-shrink: 0;
  font-size: 12px;
  overflow: hidden;
  text-align: right;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 150px;
`;

const BarContainer = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  gap: 8px;
`;

const Bar = styled.div`
  background: linear-gradient(90deg, ${p => p.theme.colors.purple400} 0%, ${p => p.theme.colors.purple300} 100%);
  border-radius: 4px;
  height: 20px;
  min-width: 4px;
  transition: width 0.3s ease;
`;

const BarValue = styled.div`
  color: ${p => p.theme.colors.gray600};
  font-size: 12px;
  font-weight: ${p => p.theme.fontWeight.medium};
  min-width: 30px;
`;
